import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { web, errMsg, toList } from "../../lib/api";
import { PageHeader, Panel, Loader, ErrorNote, Empty, shortDate } from "../../components/app/kit";
import { Field, TextInput, Chips } from "../../components/app/Form";
import { Attendance } from "../../components/app/Attendance";
import { AttendanceTaker } from "../../components/app/AttendanceTaker";
import { eventsOf, eventTitle, eventDate } from "./Overview";

const pad = (n) => String(n).padStart(2, "0");
const dayOf = (s) => {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s ?? "").slice(0, 10) : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const today = () => dayOf(new Date().toISOString());
const TYPES = [["practice", "Practice"], ["game", "Game"], ["other", "Other"]];
const keyOf = (date, eventId) => `${date}|${eventId ?? ""}`;
const fromEvent = (e) => ({ event_id: e.id, date: dayOf(eventDate(e)), event_type: ["game", "practice"].includes(e.type) ? e.type : "other", event_title: eventTitle(e) });

const SessionPicker = ({ events, session, setSession }) => (
  <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
    <Field label="Practice or game">
      <select data-testid="att-event-select" value={session.event_id ?? "custom"}
        onChange={(e) => setSession(e.target.value === "custom" ? { event_id: null, date: today(), event_type: "practice", event_title: null } : fromEvent(events.find((x) => x.id === e.target.value)))}
        className="w-full rounded-xl border border-[#1E293B] bg-[#0F172A] px-3.5 py-2.5 text-sm text-white">
        <option value="custom">Custom date (not on the schedule)</option>
        {events.map((e) => <option key={e.id} value={e.id}>{shortDate(eventDate(e))} · {e.type ?? "event"} · {eventTitle(e)}</option>)}
      </select>
    </Field>
    {!session.event_id && (
      <Field label="Date"><TextInput data-testid="att-date-input" type="date" value={session.date} onChange={(e) => setSession({ ...session, date: e.target.value })} /></Field>
    )}
    {!session.event_id && <div className="md:col-span-2"><Chips testid="att-type" options={TYPES} value={session.event_type} onChange={(v) => setSession({ ...session, event_type: v })} /></div>}
  </div>
);

const SessionList = ({ groups, onEdit, onDelete }) => (
  !groups.length ? <Empty title="No sessions yet" text="Saved practices and games show here." /> : (
    <ul className="grid gap-3 md:grid-cols-2">
      {groups.map((g) => (
        <li key={g.key} data-testid="att-session" className="rounded-xl border border-[#1E293B] bg-[#162032] px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate font-display text-lg font-bold uppercase text-white">{g.title ?? (g.type ? `${g.type}` : "Session")}</p>
              <p className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">{shortDate(g.date)}{g.type ? ` · ${g.type}` : ""} · {g.rows.length} players</p>
            </div>
            <div className="flex shrink-0 gap-1">
              <button data-testid={`att-session-edit-${g.key}`} onClick={() => onEdit(g)} className="rounded-full p-2 text-slate-300 hover:bg-[#0F172A] hover:text-[#39FF14]" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`att-session-delete-${g.key}`} onClick={() => onDelete(g)} className="rounded-full p-2 text-slate-300 hover:bg-[#0F172A] hover:text-red-300" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          <p className="mt-2 font-mono-gd text-xs text-slate-300">{["present", "late", "excused", "absent", "injured"].map((s) => `${g.rows.filter((r) => r.status === s).length} ${s}`).join(" · ")}</p>
        </li>
      ))}
    </ul>
  )
);

const group = (records) => {
  const map = {};
  records.forEach((r) => {
    const date = String(r.date ?? "").slice(0, 10);
    const k = keyOf(date, r.event_id);
    (map[k] ??= { key: k, date, event_id: r.event_id ?? null, type: r.event_type, title: r.event_title, rows: [] }).rows.push(r);
  });
  return Object.values(map).sort((a, b) => b.date.localeCompare(a.date));
};

export default function AttendancePage() {
  const { membership, inLevel } = useAuth();
  const tid = membership?.team_id;
  const [params] = useSearchParams();
  const sched = useApi(tid && `/teams/${tid}/schedule`);
  const roster = useApi(tid && `/teams/${tid}/players`);
  const att = useApi(tid && `/teams/${tid}/attendance`, web);
  const events = useMemo(() => eventsOf(sched.data).filter((e) => e.id).sort((a, b) => new Date(eventDate(b)) - new Date(eventDate(a))), [sched.data]);
  const players = useMemo(() => toList(roster.data, ["players"]).filter(inLevel), [roster.data, inLevel]);
  const levelIds = useMemo(() => new Set(players.map((p) => p.id)), [players]);
  const records = useMemo(() => (Array.isArray(att.data) ? att.data : []), [att.data]);
  const groups = useMemo(() => group(records), [records]);
  const names = useMemo(() => Object.fromEntries(toList(roster.data, ["players"]).map((p) => [p.id, `#${p.jersey ?? "–"} ${p.name}`])), [roster.data]);
  const [session, setSession] = useState({ event_id: null, date: today(), event_type: "practice", event_title: null });
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ev = events.find((e) => e.id === params.get("event"));
    if (ev) setSession(fromEvent(ev));
  }, [events, params]);

  useEffect(() => {
    const g = groups.find((x) => x.key === keyOf(session.date, session.event_id));
    setRows(g ? Object.fromEntries(g.rows.map((r) => [r.player_id, { status: r.status, note: r.note ?? "" }])) : {});
  }, [session.date, session.event_id, groups]);

  const save = async () => {
    const list = Object.entries(rows).filter(([, r]) => r.status).map(([player_id, r]) => ({ player_id, status: r.status, note: r.note || null }));
    setBusy(true);
    try {
      await web.put(`/teams/${tid}/attendance/session`, { ...session, rows: list });
      toast.success("Attendance saved");
      att.reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete attendance for ${g.title ?? shortDate(g.date)}?`)) return;
    try {
      await web.delete(`/teams/${tid}/attendance/session`, { params: { date: g.date, event_id: g.event_id ?? undefined } });
      toast.success("Session deleted");
      att.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const edit = (g) => {
    const ev = events.find((e) => e.id === g.event_id);
    setSession(ev ? fromEvent(ev) : { event_id: null, date: g.date, event_type: g.type ?? "other", event_title: g.title ?? null });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loading = sched.loading || roster.loading || att.loading;
  const error = sched.error || roster.error || att.error;
  return (
    <div>
      <PageHeader eyebrow={membership?.team_name} title="Attendance" />
      {loading && !att.data ? <Loader /> : error ? <ErrorNote error={error} /> : (
        <div className="space-y-8">
          <Panel>
            <SessionPicker events={events} session={session} setSession={setSession} />
          </Panel>
          {!players.length ? <Empty title="No players yet" text="Add players to the roster to take attendance." /> : (
            <AttendanceTaker players={players} rows={rows} setRows={setRows} onSave={save} busy={busy} />
          )}
          <Panel>
            <h2 className="mb-4 font-display text-2xl font-bold uppercase text-white">Past sessions</h2>
            <SessionList groups={groups} onEdit={edit} onDelete={remove} />
          </Panel>
          <Panel>
            <h2 className="mb-4 font-display text-2xl font-bold uppercase text-white">Season summary</h2>
            <Attendance rows={records.filter((r) => levelIds.has(r.player_id))} names={names} />
          </Panel>
        </div>
      )}
    </div>
  );
}
